import React from "react";
import { ILine, IPrice } from "@montana/models/generated/cms.interface";
import { Media, MontanaMediaRatios } from "@montana/shared/media";
import { Translate } from "@montana/shared/translate";
import { Price } from "@montana/shared/price";
import { getImageUrlFromProductImage } from "@montana/helpers/media-src.helper";
import { useTranslations } from "@montana/containers/translations";
import { GREY } from "@montana/constants/colors";
import { getDimensionTranslationValue } from "@montana/helpers/product.helper";
import { CylindoDefaultView, LDDefaultView } from "@montana/constants/images";

interface IItemSummaryProps {
    lines: ILine[] | undefined;
    totalPrice: IPrice | undefined;
    totalVatAmount: IPrice | undefined;
    dispatchTime?: string | undefined;
    deliveryPrice?: IPrice | undefined;
    showShippingCost: boolean;
}

export const ItemSummary = ({ lines, totalPrice, totalVatAmount, dispatchTime, deliveryPrice, showShippingCost }: IItemSummaryProps) => {
    const translate = useTranslations();

    return (
        <section className="item-summary">
            {lines?.map(line =>
                <div className="item-summary-line" key={`${line.code}${line.productGroupId}`}>
                    <span className="item-summary-line__img-col">
                        <Media
                            className="item-summary-line__img"
                            image={{ url: getImageUrlFromProductImage(line.productImageViewModel, GREY, line.productImageViewModel?.londonDynamicsStaticImageUrl ? LDDefaultView : CylindoDefaultView) }}
                            mediaRatio={MontanaMediaRatios.Portrait}
                            sizes="100px"
                            shouldContain={!!line.productImageViewModel?.londonDynamicsStaticImageUrl}
                        />
                    </span>
                    <div className="item-summary-line__details">
                        <div className="item-summary-line__name">{line.name}</div>
                        <div className="item-summary-line__quantity-price">
                            <div className="item-summary-line__quantity">
                                <span>
                                    <Translate value="checkoutPage.quantity" defaultValue="[QTY]" />
                                    &nbsp;
                                    {line.quantity} x {line.unitPrice?.formatted}
                                </span>
                                {(!!line.discount?.amount && line.discount?.amount > 0) && <span className="item-summary-line__quantity-before"><Price noCurrency obj={line.priceUnitWithoutDiscount} /></span>}
                            </div>
                            <div className="item-summary-line__total">
                                <Price className="item-summary-line__price" obj={line.price} />
                                {(!!line.discount?.amount && line.discount?.amount > 0) && (
                                    <span className="item-summary-line__saved-price">
                                        {translate(t => t.productPage.productPageSave, '[Save]')}
                                        &nbsp;
                                        <Price obj={line.discount} />
                                    </span>
                                )}
                            </div>
                        </div>
                        {!!line.properties ? (
                            <>
                                {line.properties.filter(p => !!p.label && !!p.text).map((property) => (
                                    <div className="item-summary-line__property" key={property.label}>
                                        <span>{property.label}:</span>
                                        <span>{getDimensionTranslationValue(translate, property.text)}</span>
                                    </div>
                                ))}
                            </>) : null}
                    </div>
                </div>
            )}
            <div className="item-summary__total">
                <Translate value="cartPage.totalLinePrice" defaultValue="[Total]" />
                <span><Price obj={totalPrice} /></span>
            </div>
            <div className="item-summary__eu-tax">
                <Translate value="cartPage.euTaxIncluded" defaultValue="[Including EU tax:]" />
                &nbsp;
                <Price className="item-summary__eu-tax-price" obj={totalVatAmount} />
            </div>
            {showShippingCost &&
                <div className="item-summary__shipping">
                    <Translate value="cartPage.cartPageShipping" defaultValue="[Shipping]" />
                    <span>
                        {deliveryPrice?.amount ? (
                            <Price className="item-summary__shipping-price" obj={deliveryPrice} />
                        ) : (
                            <Translate value="cartPage.cartPageFree" defaultValue="[Free]" />
                        )}
                    </span>
                </div>
            }
            {!!dispatchTime && (
                <div className="item-summary__estimated-delivery">
                    <Translate value="cartPage.cartPageEstimatedDelivery" defaultValue="[Estimated delivery]" />
                    <span>{dispatchTime}</span>
                </div>
            )}
        </section>
    );
};
